import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteSettings, useSocialLinks, useCustomSocialLinks } from "@/hooks/content";

const quickLinks = [
  { key: "footer.home", path: "/" },
  { key: "footer.live", path: "/live" },
  { key: "footer.news", path: "/news" },
  { key: "footer.programs", path: "/programs" },
  { key: "footer.videos", path: "/videos" },
  { key: "footer.gallery", path: "/gallery" },
  { key: "footer.schedule", path: "/schedule" },
  { key: "footer.about", path: "/about" },
  { key: "footer.contact", path: "/contact" },
];

const departmentLinks = [
  { key: "footer.news", path: "/news" },
  { key: "footer.entertainment", path: "/entertainment" },
  { key: "footer.technical", path: "/about" },
  { key: "footer.photoStudio", path: "/photo-studio" },
  { key: "footer.production", path: "/production" },
];

const legalLinks = [
  { key: "footer.privacy", path: "/privacy" },
  { key: "footer.terms", path: "/terms" },
  { key: "footer.cookies", path: "/cookies" },
  { key: "footer.disclaimer", path: "/disclaimer" },
];

export default function Footer() {
  const { t } = useTranslation();
  const { record: settings } = useSiteSettings();
  const { record: social } = useSocialLinks();
  const { items: customLinks } = useCustomSocialLinks();

  const socialLinks = [
    { name: "Facebook", icon: "ri-facebook-fill", url: social.facebook },
    { name: "YouTube", icon: "ri-youtube-fill", url: social.youtube },
    { name: "TikTok", icon: "ri-tiktok-fill", url: social.tiktok },
    { name: "Instagram", icon: "ri-instagram-fill", url: social.instagram },
    { name: "X/Twitter", icon: "ri-twitter-x-fill", url: social.twitter },
    { name: "WhatsApp", icon: "ri-whatsapp-fill", url: social.whatsapp },
    ...customLinks.map((l) => ({ name: l.name, icon: l.icon, url: l.url })),
  ].filter((s) => s.url && s.url !== "#");

  return (
    <footer className="bg-primary-900 text-white">
      {/* Main Footer */}
      <div className="ltv-container py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img
                src="https://storage.helloreaddy.io/project_files/f3582b29-89ce-4104-acc4-d71527c07beb/9c1ed9ab-5b65-494b-810b-15bf34b3d622_compressed_IMG-20260825-WA0008.webp"
                alt="L+TV TÉLÉVISION"
                className="h-12 w-auto object-contain"
              />
            </Link>
            <p className="text-white/70 text-sm leading-relaxed mb-4">
              {settings.tagline || "The Television That Connects You"}
            </p>
            <p className="text-white/50 text-sm mb-4">
              {settings.poBox ? `P.O. Box: ${settings.poBox}` : "P.O. Box: EDEA - Cameroon"}
            </p>
            <div className="flex flex-wrap gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                  className="w-9 h-9 flex items-center justify-center rounded-md bg-white/10 hover:bg-accent-500 transition-colors text-white/80 hover:text-white"
                >
                  <i className={`${social.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-white/90">
              {t("footer.quickLinks")}
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    {t(link.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Departments */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-white/90">
              {t("footer.departments")}
            </h4>
            <ul className="space-y-2.5">
              {departmentLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    {t(link.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social & Legal */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-white/90">
              {t("footer.socialMedia")}
            </h4>
            <ul className="space-y-2.5 mb-6">
              {socialLinks.map((social) => (
                <li key={social.name}>
                  <a
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-white/60 hover:text-white transition-colors flex items-center gap-2"
                  >
                    <i className={`${social.icon} text-sm`}></i>
                    {social.name}
                  </a>
                </li>
              ))}
            </ul>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-white/90">
              {t("footer.legal")}
            </h4>
            <ul className="space-y-2.5">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    {t(link.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="border-t border-white/10">
        <div className="ltv-container py-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/50">
            <p>{settings.copyright ? settings.copyright.replace("{year}", String(new Date().getFullYear())) : `© ${new Date().getFullYear()} L+TV TÉLÉVISION. ${t("footer.allRightsReserved")}`}</p>
            <p>{settings.address ? `${settings.address} — ${settings.tagline || "The Television That Connects You"}` : "Edea, Cameroon — The Television That Connects You"}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}